import { lazy, type ComponentType, type LazyExoticComponent } from 'react'

/**
 * After a deploy the old page chunks (CompanyPage-CEwmCTUY.js and friends) are gone
 * from the server. A tab opened before the deploy still asks for them on its next
 * navigation, the dynamic import fails, and the page crashes with "Failed to fetch
 * dynamically imported module" (Sentry DELIVERYAPPS-4B). The cure is one reload,
 * which fetches the new index.html and its new chunk names.
 *
 * One reload, not a loop: if the same tab already reloaded for this in the last
 * RELOAD_WINDOW_MS the chunk is genuinely missing, so we let the error through.
 */

const STORAGE_KEY = 'ch:stale-chunk-reload-at'
export const RELOAD_WINDOW_MS = 10_000

const STALE_CHUNK_MESSAGES = [
  'Failed to fetch dynamically imported module', // Chrome, Edge
  'error loading dynamically imported module', // Firefox
  'Importing a module script failed', // Safari
  'Unable to preload CSS', // Vite's CSS preload
]

export function isStaleChunkError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : ''
  return STALE_CHUNK_MESSAGES.some((m) => message.includes(m))
}

interface ReloadDeps {
  storage?: Pick<Storage, 'getItem' | 'setItem'> | null
  now?: number
  reload?: () => void
}

function sessionStore(): Pick<Storage, 'getItem' | 'setItem'> | null {
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

/**
 * Reloads the page unless this tab already did so for a stale chunk within the window.
 * Returns true when a reload was started.
 */
export function reloadForStaleChunk({
  storage = sessionStore(),
  now = Date.now(),
  reload = () => window.location.reload(),
}: ReloadDeps = {}): boolean {
  try {
    const last = Number(storage?.getItem(STORAGE_KEY) ?? 0)
    if (last && now - last < RELOAD_WINDOW_MS) {
      return false
    }
    storage?.setItem(STORAGE_KEY, String(now))
  } catch {
    // Storage blocked (private mode, tests): still reload once. Without the
    // marker a genuinely missing chunk could loop, but browsers that block
    // sessionStorage are rare and the user can stop it by navigating away.
  }
  reload()
  return true
}

/** Vite fires this when a lazy page or its CSS fails to load. */
export function installStaleChunkReload(): void {
  window.addEventListener('vite:preloadError', (event) => {
    if (reloadForStaleChunk()) {
      event.preventDefault()
    }
  })
}

type PageModule<T> = { default: T }

/**
 * `React.lazy` for a page, safe across the stale-chunk reload above.
 *
 * When the `vite:preloadError` handler cancels the error so it can reload, Vite's
 * preload helper does not reject: the dynamic import resolves to `undefined`
 * (`baseModule().catch(handlePreloadError)` returns nothing). Plain `lazy()` then
 * reads `.default` of undefined and React logs "Cannot read properties of
 * undefined (reading 'default')" in the moment before the reload lands
 * (Sentry DELIVERYAPPS-PROD-D). Here an empty module never settles instead, so the
 * page stays on its Suspense fallback until the browser navigates.
 */
export function lazyPage<T extends ComponentType<any>>(
  factory: () => Promise<PageModule<T>>,
): LazyExoticComponent<T> {
  return lazy(() =>
    factory().then((module) => module ?? new Promise<PageModule<T>>(() => {})),
  )
}
